// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Org-level subcategory cost-code catalog (table: org_cost_codes).
//
// Each org owns an editable copy of the standard subcategory list, seeded
// from DEFAULT_SUBS at org creation (migration 027). This hook is the single
// read path for "what are our standard subcategories?" — the estimate's
// subcategory picker and the rename/cost-code guards both consume it.
//
// Fallback rules, in order:
//   • No Supabase or no org (offline, sandbox, personal project) → DEFAULT_SUBS.
//   • Org row fetch failed                                       → DEFAULT_SUBS.
//   • Org has rows                                               → org rows.
//   • Org has zero rows                                          → empty catalog.
//
// That last case is deliberate: an org that deletes every entry has an empty
// catalog, and silently substituting the built-ins would resurrect entries
// they removed on purpose.

import { useState, useEffect } from "react";
import {
  fetchOrgCostCodes, BUILT_IN_BY_DIV, BUILT_IN_FLAT, type CostCodeEntry,
} from "../lib/orgCostCodes";

export type { CostCodeEntry };

export interface OrgCostCodes {
  /** Catalog grouped by division id, e.g. { div9: [{costCode:"09-250",…}] } */
  byDiv: Record<string, CostCodeEntry[]>;
  /** Flat list — convenient for "is this a standard code?" lookups. */
  all: CostCodeEntry[];
  /** True while the org fetch is in flight. */
  loading: boolean;
  /** True when the catalog came from the org table rather than the built-ins. */
  isOrgCatalog: boolean;
}

export function useOrgCostCodes(orgId: string | null | undefined): OrgCostCodes {
  const [state, setState] = useState<OrgCostCodes>({
    byDiv: BUILT_IN_BY_DIV, all: BUILT_IN_FLAT, loading: false, isOrgCatalog: false,
  });

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    // One read path, shared with the importer, so the two cannot drift.
    fetchOrgCostCodes(orgId).then(r => {
      if (cancelled) return;
      setState({ ...r, loading: false });
    });
    return () => { cancelled = true; };
  }, [orgId]);

  return state;
}
